/* Megamenu Link List — vanilla port of
 * orion-v3.0/src/js/includes/megamenu.js.
 *
 * Each .megamenu-list-js block is a 3-tier hover/focus navigation
 * (main → sub → sub-sub). The Liquid template renders all three
 * tiers in a shared 1-row grid so only the panel matching the
 * currently active link is visible at a time (via .is-active +
 * CSS opacity transitions).
 *
 * Data attributes consumed (set by blocks/_megamenu-linklist.liquid):
 *   data-default-main      Handle of the first top-level menu item;
 *                           used as the "preselect" target when the
 *                           merchant has show_sublist on.
 *   data-show-sublist      'true' | 'false' — gates the initial
 *                           activation pass.
 *   data-mega-main         A top-level menu link.
 *   data-mega-panel        Container holding one main link's children.
 *   data-nested            A sub-link.
 *   data-has-grandchildren On a sub-link that has its own children.
 *   data-mega-sub          Container holding one sub-link's children.
 *
 * Initialisation is idempotent — calling init() on an already-bound
 * .megamenu-list-js is a no-op (guarded by `data-initialized`). */

class MegamenuLinkList {
  constructor(root) {
    this.root = root;
    this.mainLinks = Array.from(root.querySelectorAll('[data-mega-main]'));
    this.panels = Array.from(root.querySelectorAll('[data-mega-panel]'));
    this.subLinks = Array.from(root.querySelectorAll('[data-nested]'));
    this.subPanels = Array.from(root.querySelectorAll('[data-mega-sub]'));
    this.defaultMain = root.dataset.defaultMain;
    this.showSublist = root.dataset.showSublist === 'true';
  }

  init() {
    if (this.root.dataset.initialized === 'true') return;

    // Set of main-link handles that actually open a panel — used to
    // narrow hover/focus binding so non-trigger links keep their
    // natural CSS :hover state without picking up a sticky .is-active
    // class that lingers after the cursor leaves.
    const triggerMainHandles = new Set(
      this.panels.map((p) => p.dataset.megaPanel)
    );

    this.mainLinks.forEach((link) => {
      if (!triggerMainHandles.has(link.dataset.megaMain)) return;
      const activate = () => this.activateMain(link.dataset.megaMain);
      link.addEventListener('mouseenter', activate);
      link.addEventListener('focus', activate);
    });

    // Same narrowing for sub-links — only the ones with grandchildren
    // open a sub-sub panel, so only they should switch active state.
    // Non-trigger sub-links keep their natural :hover only.
    this.subLinks.forEach((link) => {
      if (link.dataset.hasGrandchildren !== 'true') return;
      const activate = () => this.activateSubPanel(link.dataset.nested);
      link.addEventListener('mouseenter', activate);
      link.addEventListener('focus', activate);
    });

    // Preselect the cached main link so the deepest panel is visible
    // straight away when the megamenu opens (only when show_sublist
    // is on — otherwise there's nothing to preselect).
    const initialMain = this.defaultMain
      || (this.mainLinks[0] && this.mainLinks[0].dataset.megaMain);
    if (initialMain && this.showSublist) {
      this.activateMain(initialMain);
    }

    // Listen for the enclosing megamenu's close event so we can
    // wipe link / panel state and return to the initial pass.
    // megamenu.js dispatches `megamenu:closed` whenever a megamenu
    // loses its .is-active class (closeAll + cross-trigger switch).
    const megamenuEl = this.root.closest('[data-megamenu]');
    if (megamenuEl) {
      megamenuEl.addEventListener('megamenu:closed', () => this.reset());
    }

    this.root.dataset.initialized = 'true';
  }

  reset() {
    // Wipe every active state in one pass.
    this.mainLinks.forEach((link) => link.classList.remove('is-active'));
    this.panels.forEach((panel) => panel.classList.remove('is-active'));
    this.deactivateSubPanels();

    // Re-apply the default-main preselect so the next open starts
    // identical to the first open (rather than blank when
    // showSublist is on).
    const initialMain = this.defaultMain
      || (this.mainLinks[0] && this.mainLinks[0].dataset.megaMain);
    if (initialMain && this.showSublist) {
      this.activateMain(initialMain);
    }
  }

  deactivateSubPanels() {
    this.subPanels.forEach((panel) => panel.classList.remove('is-active'));
    this.subLinks.forEach((link) => link.classList.remove('is-active'));
  }

  activateSubPanel(handle) {
    if (!handle) {
      this.deactivateSubPanels();
      return;
    }
    this.subPanels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.megaSub === handle);
    });
    this.subLinks.forEach((link) => {
      link.classList.toggle('is-active', link.dataset.nested === handle);
    });
  }

  activateMain(handle) {
    if (!handle) return;

    // Only paint .is-active on the matching link when it actually
    // opens a panel. Belt-and-braces against forced calls (default-
    // main preselect, reset) with a non-trigger handle — the hover/
    // focus handlers already narrow to triggers, but the preselect
    // can target whatever the merchant set as the default.
    const hasPanel = this.panels.some(
      (p) => p.dataset.megaPanel === handle
    );

    this.panels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.megaPanel === handle);
    });
    this.mainLinks.forEach((link) => {
      link.classList.toggle(
        'is-active',
        hasPanel && link.dataset.megaMain === handle
      );
    });

    // Always clear any sub-panel + sub-link state when switching to
    // a different main. Column 3 only appears when the user
    // explicitly hovers / focuses a sub-link that has grandchildren.
    this.deactivateSubPanels();
  }
}

function start() {
  document.querySelectorAll('.megamenu-list-js').forEach((root) => {
    new MegamenuLinkList(root).init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
